import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import SessionManager from '../session/sessionManager';
import { Dropdown } from 'semantic-ui-react';
import { UUID } from '../utils/utils';

const LANGUAGES = {
    "fr-fr": "Français (France)",
    "fr-ca": "Français (Canada)",
    "en-gb": "English (United Kingdom)",
    "en-ca": "English (Canada)",
    "en-us": "English (United States)",
    "es-es": "Español",
    "ca-es": "Català",
    "it-it": "Italiano",
    "br": "Brezhoneg",
    "nl-nl": "Netherlands",
    "de-de": "Deutsch",
    "eu": "Euskara"
};

/*
Default is en-GB

If language is chosen by user:
    - use user-chosen language

If language is set on URL
    - wait for widget response and choose language from url if available

Else i18next will pick default lang
*/

const LanguageSelector = ({ langs=[], currentLang, changeLanguage, display=true }) => {
    const {t} = useTranslation();
    if(langs.length < 1 || !display){
        return null;
    }

    return (
        <div className='language-manager__language-selector' style={{ textAlign: 'left', padding: '0 15px' }}>
            <Dropdown
                aria-label={t('widget.language.change-warning')}
                button
                className='icon'
                icon='globe'
                labeled
                floating
                selection
                onChange={changeLanguage}
                value={currentLang}
                options={langs}
            />
        </div>
    );
};

const getLanguageOptions = (widget) => {
    let languageArray = [];
    let widgetLanguages = widget.languages.map(l => l.toLowerCase());
    Object.entries(LANGUAGES).forEach(([key, value]) => {
        if(widgetLanguages && widgetLanguages.includes(key)){
            languageArray.push({
                text: value,
                value: key
            });
        }
    });
    return languageArray;
};

function formatLocale(lang) {
    if (lang.split("-").length === 1) {
        if (lang === 'en') {
            return `${lang}-gb`
        }
        return `${lang}-${lang}`
    }

    return lang;
}

/**
 * We now manage the widget locale only with this xx-xx format (ex: en-gb), everywhere in Neo
 * What is returned by the backend is not yet harmonised in this xx-xx format,
 * Neo ensures compatibility by not being case-sensitive.
 */


export default function LanguageManager({ widgetKey }) {
    // Tab ID is handled here to avoid changing it later if renders change
    const uuid = React.useRef(UUID()).current;

    // Default language is from
    let url_params = new URLSearchParams(window.location.search);
    let url_lang = url_params.get('locale');

    if(url_lang) {
        url_lang = url_lang.replace('_', '-');
    }

    const { i18n } = useTranslation();

    const [ lang, setLang ] = React.useState(formatLocale(url_lang || window.navigator.language.toLowerCase()));
    const [ availableLangs, setAvailableLangs ] = React.useState([]);
    const [ userSelectedLang, setUserSelectedLang ] = React.useState(null);

    const [ widget, setWidget ] = React.useState(null);

    const _setLang = React.useCallback((lang) => {
        setLang(lang);
        document.documentElement.setAttribute('lang',lang);
        i18n.changeLanguage(lang);
        moment.locale(i18n.language);
    }, [ i18n ]);

    const changeLanguage = React.useCallback(( event, { value } )=> {
        _setLang(value);
        setUserSelectedLang(true);
        i18n.changeLanguage(value);
    }, [_setLang, setUserSelectedLang, i18n]);

    const onWidget = React.useCallback(new_widget => {
        // Prevent useless re-renders if widget is already present
        if(widget) {
            if(widget.widget_key === new_widget.widget_key) {
                return;
            }
        }

        //We consider URL lang as absolute truth unless user selected a language
        if(url_lang && !userSelectedLang) {
            document.documentElement.setAttribute('lang', url_lang);
            _setLang(lang);
            return setWidget(new_widget);
        }

        let { default_language='en-gb' } = new_widget;

        setAvailableLangs(getLanguageOptions(new_widget));

        function widgetHasLang(lang, availableLanguages) {
            return availableLanguages.some(_l => _l.toLowerCase() === lang)
        }

        if(!userSelectedLang) {
            //Apply the language if it is available in the widget,
            //otherwise apply the widget's default language.
            if (widgetHasLang(lang, new_widget.languages)) {
                _setLang(lang)
            } else {
                _setLang(default_language.toLowerCase() || 'en-gb');
            }
        }

        setWidget(new_widget);
    }, [ widget, _setLang, url_lang, lang, userSelectedLang, i18n ]);

    let { displayLanguageSelector=false } = widget?.customs || {};

    return (
        <React.Fragment>
            <SessionManager
                widgetKey={widgetKey}
                lang={lang}
                changeLanguage={changeLanguage}
                onWidget={onWidget}
                uuid={uuid}
            />
            <LanguageSelector
                langs={availableLangs}
                currentLang={lang}
                changeLanguage={changeLanguage}
                display={displayLanguageSelector}
            />
        </React.Fragment>
    );
}
